/** Products shown on the post-signup plan selection step. */

import { sanitizeAppReturnPath } from "util/routeAccess";

export const FRONT_DOOR_PRODUCT_KEYS = [
  "survey",
  "sonic_prospect",
  "sonic_prospect_channels",
  "sonic_prospect_ecommerce",
  "advisor",
];

export const WIZARD_PRODUCT_KEYS = [
  "survey",
  "sonic_prospect_channels",
  "sonic_prospect_ecommerce",
  "voice_of_customer",
  "market_intelligence",
  "full_intelligence",
];

/** URL / marketing aliases → canonical product_key (API + SIGNUP_PRODUCTS). */
const SIGNUP_PRODUCT_URL_ALIASES = {
  survey: "survey",
  "sonic-prospect": "sonic_prospect",
  sonic_prospect: "sonic_prospect",
  "sonic-prospect-channels": "sonic_prospect_channels",
  sonic_prospect_channels: "sonic_prospect_channels",
  channels: "sonic_prospect_channels",
  "prospect-channels": "sonic_prospect_channels",
  "sonic-prospect-ecommerce": "sonic_prospect_ecommerce",
  sonic_prospect_ecommerce: "sonic_prospect_ecommerce",
  ecommerce: "sonic_prospect_ecommerce",
  "prospect-ecommerce": "sonic_prospect_ecommerce",
  voc: "voice_of_customer",
  "voice-of-customer": "voice_of_customer",
  voice_of_customer: "voice_of_customer",
  market: "market_intelligence",
  mi: "market_intelligence",
  "market-intelligence": "market_intelligence",
  market_intelligence: "market_intelligence",
  "full-intelligence": "full_intelligence",
  full_intelligence: "full_intelligence",
  "customer-365": "customer_365",
  customer365: "customer_365",
  c365: "customer_365",
  customer_365: "customer_365",
  advisor: "advisor",
  "sonic-advisor": "advisor",
  sonic_advisor: "advisor",
  cxgpt: "advisor",
  "cx-gpt": "advisor",
  sonic: "sonic",
  "sonic-enterprise": "full_intelligence",
  sonic_enterprise: "full_intelligence",
  engage: "sonic",
  "sonic-engage": "sonic",
  sonic_engage: "sonic",
  triage: "voice_of_customer",
  "ai-ticket-triage": "voice_of_customer",
  "ticket-triage": "voice_of_customer",
  ai_ticket_triage: "voice_of_customer",
};

export const SIGNUP_PRODUCTS = [
  {
    key: "survey",
    labelKey: "signup_products.survey.label",
    descKey: "signup_products.survey.desc",
    selfServe: true,
    badgeKey: "signup_products.survey.badge",
  },
  {
    key: "sonic_prospect",
    labelKey: "signup_products.sonic_prospect.label",
    descKey: "signup_products.sonic_prospect.desc",
    selfServe: true,
    badgeKey: "signup_products.sonic_prospect.badge",
  },
  {
    key: "sonic_prospect_channels",
    labelKey: "signup_products.sonic_prospect_channels.label",
    descKey: "signup_products.sonic_prospect_channels.desc",
    selfServe: true,
    badgeKey: "signup_products.start_now",
  },
  {
    key: "sonic_prospect_ecommerce",
    labelKey: "signup_products.sonic_prospect_ecommerce.label",
    descKey: "signup_products.sonic_prospect_ecommerce.desc",
    selfServe: true,
    badgeKey: "signup_products.start_now",
  },
  {
    key: "voice_of_customer",
    labelKey: "signup_products.voc.label",
    descKey: "signup_products.voc.desc",
    selfServe: false,
  },
  {
    key: "market_intelligence",
    labelKey: "signup_products.market.label",
    descKey: "signup_products.market.desc",
    selfServe: false,
  },
  {
    key: "full_intelligence",
    labelKey: "signup_products.full_intelligence.label",
    descKey: "signup_products.full_intelligence.desc",
    selfServe: false,
  },
  {
    key: "customer_365",
    labelKey: "signup_products.customer365.label",
    descKey: "signup_products.customer365.desc",
    selfServe: false,
  },
  {
    key: "advisor",
    labelKey: "signup_products.advisor.label",
    descKey: "signup_products.advisor.desc",
    selfServe: true,
    badgeKey: "signup_products.advisor.badge",
  },
  {
    key: "sonic",
    labelKey: "signup_products.sonic.label",
    descKey: "signup_products.sonic.desc",
    selfServe: false,
  },
];

export function normalizeSignupProductKey(raw) {
  const key = (raw || "").toLowerCase().trim();
  if (!key) return null;
  if (SIGNUP_PRODUCT_URL_ALIASES[key]) {
    return SIGNUP_PRODUCT_URL_ALIASES[key];
  }
  if (SIGNUP_PRODUCTS.some((p) => p.key === key)) {
    return key;
  }
  return null;
}

export function getSignupProductByKey(productKey) {
  return SIGNUP_PRODUCTS.find((p) => p.key === productKey) || null;
}

export function isFrontDoorSignupProduct(productKey) {
  return FRONT_DOOR_PRODUCT_KEYS.includes(productKey);
}

export function wizardSignupProducts() {
  return WIZARD_PRODUCT_KEYS.map((key) => getSignupProductByKey(key)).filter(
    Boolean
  );
}

/** Limit the chooser to the requested product so Prospect does not sit next to Survey. */
export function signupProductsForIntent(productKey) {
  const key = normalizeSignupProductKey(productKey);
  if (!key) return wizardSignupProducts();
  const match = SIGNUP_PRODUCTS.filter((p) => p.key === key);
  return match.length ? match : wizardSignupProducts();
}

/**
 * Product for post-signup screens. No URL / stored intent means the fit
 * wizard — do not lock a verified account onto Sonic Prospect.
 */
export function resolveOnboardingProduct({
  urlParams = {},
  intendedProduct,
} = {}) {
  return (
    resolveSignupProductFromUrl(urlParams) ||
    normalizeSignupProductKey(intendedProduct) ||
    readSignupProductIntent()
  );
}

export function isPaidSignupProduct(productKey) {
  const product = getSignupProductByKey(productKey);
  return Boolean(product && !product.selfServe);
}

/**
 * Resolve intended product from signup URL.
 * Prefer explicit ?product=; fall back to utm_campaign heuristics.
 */
export function resolveSignupProductFromUrl(params = {}) {
  const direct = normalizeSignupProductKey(params.product);
  if (direct) {
    return direct;
  }
  const campaign = (params.utm_campaign || "").toLowerCase();
  if (!campaign) {
    return null;
  }
  // Front-door first (more specific sonic-* before generic sonic)
  if (
    campaign.includes("prospect-channels") ||
    campaign.includes("prospect_channels") ||
    campaign.includes("sonic-prospect-channels")
  ) {
    return "sonic_prospect_channels";
  }
  if (
    campaign.includes("prospect-ecommerce") ||
    campaign.includes("prospect_ecommerce") ||
    campaign.includes("sonic-prospect-ecommerce")
  ) {
    return "sonic_prospect_ecommerce";
  }
  if (campaign.includes("sonic-prospect") || campaign.includes("sonic_prospect")) {
    return "sonic_prospect";
  }
  if (campaign.includes("survey")) {
    return "survey";
  }
  if (
    campaign.includes("sonic-engage") ||
    campaign.includes("sonic_engage") ||
    campaign.includes("pivony-sonic")
  ) {
    return "sonic";
  }
  if (
    campaign.includes("sonic-advisor") ||
    campaign.includes("sonic_advisor") ||
    campaign.includes("cxgpt") ||
    campaign.includes("cx-gpt")
  ) {
    return "advisor";
  }
  if (campaign.includes("full-intelligence") || campaign.includes("full_intelligence")) {
    return "full_intelligence";
  }
  if (
    campaign.includes("market-intelligence") ||
    campaign.includes("market_intelligence") ||
    campaign.includes("market-intel")
  ) {
    return "market_intelligence";
  }
  if (
    campaign.includes("customer-365") ||
    campaign.includes("customer_365") ||
    campaign.includes("customer365")
  ) {
    return "customer_365";
  }
  if (
    campaign.includes("voice-of-customer") ||
    campaign.includes("voice_of_customer") ||
    /(^|[^a-z])voc([^a-z]|$)/.test(campaign)
  ) {
    return "voice_of_customer";
  }
  if (campaign.includes("triage") || campaign.includes("ai-ticket")) {
    return "voice_of_customer";
  }
  if (campaign.includes("advisor")) {
    return "advisor";
  }
  if (campaign.includes("engage") || /(^|[^a-z])sonic([^a-z]|$)/.test(campaign)) {
    return "sonic";
  }
  if (campaign.includes("market")) {
    return "market_intelligence";
  }
  return null;
}

export const SIGNUP_PRODUCT_INTENT_KEY = "pivony_signup_product";

export function persistSignupProductIntent(productKey) {
  if (typeof window === "undefined") return;
  const key = normalizeSignupProductKey(productKey);
  if (!key) return;
  try {
    window.localStorage.setItem(SIGNUP_PRODUCT_INTENT_KEY, key);
  } catch {
    // ignore quota / private-mode failures
  }
}

export function readSignupProductIntent() {
  if (typeof window === "undefined") return null;
  try {
    return normalizeSignupProductKey(
      window.localStorage.getItem(SIGNUP_PRODUCT_INTENT_KEY)
    );
  } catch {
    return null;
  }
}

export function signupProductQueryValue(productKey) {
  const key = normalizeSignupProductKey(productKey);
  if (!key) return null;
  if (key === "sonic_prospect") return "sonic-prospect";
  if (key === "sonic_prospect_channels") return "sonic-prospect-channels";
  if (key === "sonic_prospect_ecommerce") return "sonic-prospect-ecommerce";
  return key;
}

export function buildSignupPath(productKey, lang) {
  const key =
    normalizeSignupProductKey(productKey) || readSignupProductIntent();
  const query = signupProductQueryValue(key);
  let path = "/signup";
  if (query) {
    path = `/signup?product=${encodeURIComponent(query)}`;
  }
  const code = String(lang || "").trim();
  if (!code) return path;
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}lang=${encodeURIComponent(code)}`;
}

export function isVerificationPath(path) {
  if (!path) return false;
  const pathname = path.split("?")[0].split("#")[0].toLowerCase();
  return pathname === "/verification" || pathname.startsWith("/verification/");
}

/**
 * After email verify: keep a real in-app continue URL, never loop on
 * /verification, and restore the signup product deep-link when needed.
 */
export function resolvePostEmailVerificationPath({
  continueUrl,
  hasPlan = false,
  origin,
  intendedProduct,
} = {}) {
  const continuePath = continueUrl
    ? sanitizeAppReturnPath(continueUrl, origin)
    : null;
  const continueIsVerification = isVerificationPath(continuePath);
  const continueIsSignup = continuePath
    ? continuePath.split("?")[0].split("#")[0].toLowerCase() === "/signup"
    : false;

  if (hasPlan) {
    if (continuePath && !continueIsVerification && !continueIsSignup) {
      return continuePath;
    }
    return "/home";
  }

  if (continuePath && !continueIsVerification) {
    return continuePath;
  }
  return buildSignupPath(intendedProduct);
}

export function buildSignupSalesCtaUrl(baseUrl, productKey) {
  const url = new URL(baseUrl);
  url.searchParams.set("utm_source", "app.pivony.com");
  url.searchParams.set("utm_medium", "signup");
  url.searchParams.set(
    "utm_campaign",
    productKey || "sales_trial"
  );
  url.searchParams.set("utm_content", "post_signup_sales_cta");
  return url.toString();
}
